/* One product's PostHog pulse: key events (24h vs 7-day average, 30 daily bars), its main funnel, and every tile of
   its daily PostHog dashboard. Nothing is defined here: counts come from productPulse, tiles are PostHog's own
   insights, and every title links out to PostHog for the real exploring. The pulse renders from the home cache at
   once; the dashboard can take seconds to refresh, so it loads separately. */
import { EPosthogTileKind } from "@aix/aixinternal-shared/enums"
import { Link, useParams } from "@tanstack/react-router"
import type { inferRouterOutputs } from "@trpc/server"
import { ArrowLeft, ExternalLink } from "lucide-react"
import type { ReactNode } from "react"

import type { AppRouter } from "../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../lib/trpc"
import { ago } from "../HomeDashboardScreen/homeDashboardModel"
import { EVerdict } from "../HomeDashboardScreen/homeDashboard.types"
import { deltaPct, formatDelta, pulseVerdict, referenceDaily } from "../HomeDashboardScreen/widgets/productPulseModel"
import { DailyBars, FunnelBars, Sparkline, seriesColor } from "./PulseCharts"

type TDetail = inferRouterOutputs<AppRouter>["productPulse"]["getDetail"]
type TProduct = TDetail["product"]
type TTile = NonNullable<TDetail["dashboard"]>["tiles"][number]

const REFETCH_MS = 5 * 60_000
const QUERY_OPTIONS = { refetchInterval: REFETCH_MS, trpc: { context: { skipBatch: true } } } as const

const VERDICT_TEXT: Record<EVerdict, string> = {
  [EVerdict.ACT]: "text-destructive",
  [EVerdict.WATCH]: "text-[hsl(var(--chart-4))]",
  [EVerdict.UNKNOWN]: "text-muted-foreground",
  [EVerdict.CLEAR]: "text-muted-foreground",
}

function OutLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
      {children}
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
    </a>
  )
}

function Card({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-md border border-border bg-background p-3">
      <h3 className="mb-2 text-sm font-medium">{title}</h3>
      {children}
    </section>
  )
}

function MetricCard({ product, metric }: { product: TProduct; metric: TProduct["metrics"][number] }) {
  const reference = referenceDaily(metric)
  return (
    <Card title={metric.label}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-2xl font-semibold tabular-nums">{metric.current.toLocaleString()}</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatDelta(deltaPct(metric.current, reference))} vs {reference.toLocaleString()}/day
        </span>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        last 24h · 7-day avg {metric.baselineDaily.toLocaleString()}/day
        {metric.expectedDaily ? ` · expected ≥${metric.expectedDaily}/day` : ""}
      </p>
      <DailyBars days={product.days} values={metric.daily} label={metric.label} />
    </Card>
  )
}

function TileCard({ tile }: { tile: TTile }) {
  const title = <OutLink href={tile.url}>{tile.name}</OutLink>
  if (tile.kind === EPosthogTileKind.FUNNEL) {
    return (
      <Card title={title}>
        <FunnelBars steps={tile.steps} />
      </Card>
    )
  }
  if (tile.kind === EPosthogTileKind.TRENDS) {
    return (
      <Card title={title}>
        <ul className="space-y-1.5">
          {tile.series.map((s, i) => (
            <li key={s.label} className="flex items-center justify-between gap-2 text-xs">
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              <Sparkline values={s.data} color={seriesColor(i)} />
              <span className="w-24 text-right tabular-nums">
                {s.total.toLocaleString()}
                {s.previousTotal !== null && (
                  <span className="text-muted-foreground"> ({formatDelta(deltaPct(s.total, s.previousTotal))})</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    )
  }
  return (
    <Card title={title}>
      <p className="text-xs text-muted-foreground">
        {tile.queryKind.replace(/Query$/, "")} insight. Open it in PostHog to see it.
      </p>
    </Card>
  )
}

function Dashboard({ data, isLoading, error }: { data?: TDetail; isLoading: boolean; error?: string }) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading the PostHog dashboard (it can take a few seconds)…</p>
  }
  if (error) return <p className="text-sm text-destructive">{error}</p>
  const dashboard = data?.dashboard
  if (!dashboard) {
    return <p className="text-sm text-destructive">{data?.dashboardError ?? "Dashboard unavailable."}</p>
  }
  const refreshed = dashboard.tiles.map((t) => t.lastRefresh).find(Boolean)
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">
          <OutLink href={dashboard.url}>{dashboard.name}</OutLink>
        </h2>
        <span className="text-xs text-muted-foreground">
          {dashboard.tiles.length} insights{refreshed ? ` · computed ${ago(refreshed, Date.now())}` : ""}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {dashboard.tiles.map((tile) => (
          <TileCard key={tile.insightId} tile={tile} />
        ))}
      </div>
    </section>
  )
}

export default function ProductPulseScreen() {
  const { company } = useParams({ strict: false }) as { company: string }
  const pulse = trpc.productPulse.get.useQuery(undefined, QUERY_OPTIONS)
  const detail = trpc.productPulse.getDetail.useQuery({ company }, QUERY_OPTIONS)
  // The detail carries a freshly refreshed funnel; until it lands, the home pulse renders the page
  const product = detail.data?.product ?? pulse.data?.products.find((p) => p.company === company)

  if (!product) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {pulse.isLoading ? "Loading…" : `No PostHog pulse for "${company}".`}
      </div>
    )
  }

  const { verdict, reason } = pulseVerdict(product)
  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <header className="space-y-2">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline">
          <ArrowLeft className="h-3 w-3" /> Home
        </Link>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="text-xl font-semibold">{product.label} pulse</h1>
          <OutLink href={product.url}>Open {product.label} in PostHog</OutLink>
        </div>
        <p className={`text-sm ${VERDICT_TEXT[verdict]}`}>{reason}</p>
        <nav className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="PostHog dashboards">
          {product.dashboards.map((d) => (
            <OutLink key={d.url} href={d.url}>
              {d.label}
            </OutLink>
          ))}
        </nav>
        {(product.error ?? product.warning) && (
          <p className="text-xs text-destructive">{product.error ?? product.warning}</p>
        )}
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {product.metrics.map((metric) => (
          <MetricCard key={metric.event} product={product} metric={metric} />
        ))}
      </div>

      {product.funnel && (
        <Card
          title={
            <span className="flex flex-wrap items-baseline justify-between gap-2">
              <OutLink href={product.funnel.url}>{product.funnel.name}</OutLink>
              {product.funnel.lastRefresh && (
                <span className="text-xs font-normal text-muted-foreground">
                  computed {ago(product.funnel.lastRefresh, Date.now())}
                </span>
              )}
            </span>
          }
        >
          <FunnelBars steps={product.funnel.steps} />
        </Card>
      )}

      <Dashboard data={detail.data} isLoading={detail.isLoading} error={detail.error?.message} />
    </div>
  )
}
